import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Plus, Phone, Mail, User, Check, MessageSquare, MoreVertical, Trash2 } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

function relativeTime(value) {
  if (!value) return '';
  const days = Math.floor((Date.now() - new Date(value).getTime()) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 14) return `${days}d ago`;
  return new Date(value).toLocaleDateString();
}

function formatWantLine(criteria) {
  if (!criteria || typeof criteria !== 'object') return null;
  const parts = [];
  if (criteria.bedrooms_min != null) parts.push(`${criteria.bedrooms_min} bed`);
  if (criteria.price_max != null && Number.isFinite(Number(criteria.price_max))) {
    parts.push(`up to $${Number(criteria.price_max).toLocaleString()}`);
  }
  const areas = Array.isArray(criteria.areas)
    ? criteria.areas.filter(Boolean).join(', ')
    : (criteria.areas || null);
  if (areas) parts.push(areas);
  return parts.length ? parts.join(' · ') : null;
}

function formatContextLine(lead) {
  if (!lead.notes) return null;
  const n = String(lead.notes).trim();
  return n.length > 120 ? `${n.slice(0, 117)}…` : n;
}

async function copyContact(value, label) {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  } catch {
    toast.error('Could not copy');
  }
}

export default function Leads() {
  const [searchParams] = useSearchParams();
  const highlightLeadId = searchParams.get('id');
  const highlightRef = useRef(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');
  // All leads, with a contacted badge. Never-contacted stays available as a filter.
  const [filter, setFilter] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [newLead, setNewLead] = useState({ first_name: '', last_name: '', email: '', phone: '', lead_type: 'buyer' });
  const [openMenuId, setOpenMenuId] = useState(null);
  const [sendingEmailId, setSendingEmailId] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!openMenuId) return undefined;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpenMenuId(null);
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setOpenMenuId(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [openMenuId]);

  const fetchLeads = () => {
    setLoadError('');
    api.get('/leads')
      .then(res => setLeads(res.data.leads))
      .catch(() => {
        setLeads([]);
        setLoadError('Could not load leads.');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchLeads(); }, []);

  useEffect(() => {
    if (!highlightLeadId || loading || !highlightRef.current) return;
    highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightLeadId, loading, leads]);

  const handleAddLead = async (e) => {
    e.preventDefault();
    try {
      await api.post('/leads', { ...newLead, source: 'manual' });
      toast.success('Lead added!');
      setShowAdd(false);
      setNewLead({ first_name: '', last_name: '', email: '', phone: '', lead_type: 'buyer' });
      fetchLeads();
    } catch (err) {
      toast.error('Failed to add lead');
    }
  };

  const handleDeleteLead = async (lead) => {
    const name = `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || lead.email || 'this lead';
    if (!window.confirm(
      `Delete the lead "${name}"?\n\nThis removes the lead entry only — if they have a registered client account, their profile and conversations are not affected. This cannot be undone.`
    )) return;
    const prev = leads;
    setLeads((ls) => ls.filter((l) => l.id !== lead.id));
    try {
      await api.delete(`/leads/${lead.id}`);
      toast.success(`Deleted lead: ${name}`);
    } catch (err) {
      setLeads(prev);
      toast.error('Failed to delete lead');
    }
  };

  const handleMarkContacted = async (leadId) => {
    const prev = leads;
    const now = new Date().toISOString();
    setLeads(ls => ls.map(l => (l.id === leadId ? { ...l, last_contacted_at: now, needs_followup: false } : l)));
    try {
      await api.patch(`/leads/${leadId}`, { mark_contacted: true });
      toast.success('Marked as contacted');
    } catch (err) {
      setLeads(prev);
      toast.error('Failed to update');
    }
  };

  const filtered = leads.filter(l => {
    if (filter === 'never_contacted' && l.last_contacted_at) return false;
    if (search === '') return true;
    return (
      `${l.first_name} ${l.last_name}`.toLowerCase().includes(search.toLowerCase()) ||
      l.email?.toLowerCase().includes(search.toLowerCase()) ||
      l.phone?.includes(search) ||
      l.notes?.toLowerCase().includes(search.toLowerCase())
    );
  });

  return (
    <div>
      <div className="page-header" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
        <div>
          <h2>Leads</h2>
          <p>{filtered.length} shown · {leads.length} total</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowAdd(!showAdd)}>
          <Plus size={15} /> Add Lead
        </button>
      </div>

      {showAdd && (
        <div className="card" style={{marginBottom: 24}}>
          <div className="card-header">
            <span className="card-title">New Lead</span>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowAdd(false)}>Cancel</button>
          </div>
          <form onSubmit={handleAddLead}>
            <div className="grid-2" style={{marginBottom: 12}}>
              <div className="form-group" style={{marginBottom: 0}}>
                <label className="form-label" htmlFor="lead-first-name">First Name</label>
                <input id="lead-first-name" className="form-input" value={newLead.first_name} onChange={e => setNewLead(l => ({...l, first_name: e.target.value}))} required />
              </div>
              <div className="form-group" style={{marginBottom: 0}}>
                <label className="form-label" htmlFor="lead-last-name">Last Name</label>
                <input id="lead-last-name" className="form-input" value={newLead.last_name} onChange={e => setNewLead(l => ({...l, last_name: e.target.value}))} />
              </div>
            </div>
            <div className="grid-2" style={{marginBottom: 12}}>
              <div className="form-group" style={{marginBottom: 0}}>
                <label className="form-label" htmlFor="lead-email">Email</label>
                <input id="lead-email" className="form-input" type="email" value={newLead.email} onChange={e => setNewLead(l => ({...l, email: e.target.value}))} />
              </div>
              <div className="form-group" style={{marginBottom: 0}}>
                <label className="form-label" htmlFor="lead-phone">Phone</label>
                <input id="lead-phone" className="form-input" value={newLead.phone} onChange={e => setNewLead(l => ({...l, phone: e.target.value}))} />
              </div>
            </div>
            <div className="form-group" style={{marginBottom: 16}}>
              <label className="form-label" htmlFor="lead-type">Type</label>
              <select id="lead-type" className="form-input" value={newLead.lead_type} onChange={e => setNewLead(l => ({...l, lead_type: e.target.value}))}>
                <option value="buyer">Buyer</option>
                <option value="seller">Seller</option>
                <option value="both">Both</option>
              </select>
            </div>
            <button type="submit" className="btn btn-primary">Save Lead</button>
          </form>
        </div>
      )}

      <div style={{display: 'flex', gap: 10, marginBottom: 16}}>
        <div className="search-bar" style={{flex: 1, marginBottom: 0}}>
          <Search size={16} color="var(--text-muted)" />
          <input placeholder="Search leads..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-input" style={{width: 'auto', padding: '8px 12px'}} value={filter} onChange={e => setFilter(e.target.value)}>
          <option value="">All leads</option>
          <option value="never_contacted">Never contacted</option>
        </select>
      </div>

      {loading ? (
        <div className="empty-state"><div className="spinner" /></div>
      ) : loadError ? (
        <div className="empty-state card">
          <p>{loadError}</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty-state card">
          <User size={32} color="var(--text-muted)" />
          <p>{filter === 'never_contacted' && leads.length > 0 ? 'Everyone here has been contacted.' : 'No leads found. Add your first lead above!'}</p>
        </div>
      ) : (
        <div className="leads-card-list">
          {filtered.map(lead => {
            const want = formatWantLine(lead.buyer_criteria);
            const context = formatContextLine(lead);
            const neverContacted = !lead.last_contacted_at;
            const sellerBadge = lead.lead_type === 'seller' || lead.lead_type === 'both';
            const isHighlighted = highlightLeadId && String(lead.id) === String(highlightLeadId);
            return (
              <article
                key={lead.id}
                ref={isHighlighted ? highlightRef : undefined}
                className={`leads-card${isHighlighted ? ' leads-card--highlight' : ''}`}
              >
                <div className="leads-card__main">
                  <div className="leads-card__title-row">
                    <span className="leads-card__name">{lead.first_name} {lead.last_name}</span>
                    <select
                      className="form-input"
                      style={{ width: 'auto', padding: '2px 8px', fontSize: 12 }}
                      value={lead.status || 'new'}
                      aria-label={`Status for ${lead.first_name}`}
                      onChange={async (e) => {
                        const status = e.target.value;
                        try {
                          await api.patch(`/leads/${lead.id}`, { status });
                          setLeads((prev) => prev.map((row) => (row.id === lead.id ? { ...row, status } : row)));
                        } catch {
                          toast.error('Could not update that lead');
                        }
                      }}
                    >
                      <option value="new">New</option>
                      <option value="active">Active</option>
                      <option value="qualified">Qualified</option>
                      <option value="closed">Closed</option>
                    </select>
                    {neverContacted ? (
                      <span className="leads-badge leads-badge--warn">Never contacted</span>
                    ) : (
                      <span className="leads-badge leads-badge--ok">Contacted</span>
                    )}
                    {sellerBadge && (
                      <span className="leads-badge leads-badge--ok">Seller</span>
                    )}
                    <span className="leads-card__time">{relativeTime(lead.created_at)}</span>
                  </div>
                  {want && <div className="leads-card__want">{want}</div>}
                  {(lead.phone || lead.email) && (
                    <div className="leads-card__contact">
                      {lead.phone && <span>{lead.phone}</span>}
                      {lead.email && <span>{lead.email}</span>}
                    </div>
                  )}
                  {context && <div className="leads-card__context">{context}</div>}
                </div>
                <div className="leads-card__actions">
                  {lead.phone && (
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => copyContact(lead.phone, 'Number')}>
                      <Phone size={14} /> Copy number
                    </button>
                  )}
                  {lead.email && (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      disabled={sendingEmailId === lead.id}
                      onClick={async () => {
                        setSendingEmailId(lead.id);
                        try {
                          const res = await api.post(`/leads/${lead.id}/email`);
                          toast.success(`Email sent to ${res.data.to}`);
                          fetchLeads();
                        } catch (err) {
                          toast.error(err?.response?.data?.error || 'Could not send email');
                        } finally {
                          setSendingEmailId(null);
                        }
                      }}
                    >
                      <Mail size={14} /> {sendingEmailId === lead.id ? 'Sending…' : 'Send email'}
                    </button>
                  )}
                  {lead.latest_conversation_id && (
                    <Link
                      className="btn btn-ghost btn-sm"
                      to={`/chat?conv=${lead.latest_conversation_id}`}
                    >
                      <MessageSquare size={14} /> View chat
                    </Link>
                  )}
                  {neverContacted && (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => handleMarkContacted(lead.id)}
                    >
                      <Check size={14} /> Mark contacted
                    </button>
                  )}
                  <div style={{ position: 'relative', display: 'inline-block' }} ref={openMenuId === lead.id ? menuRef : undefined}>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => setOpenMenuId((id) => (id === lead.id ? null : lead.id))}
                      title="More actions"
                      aria-haspopup="true"
                      aria-expanded={openMenuId === lead.id}
                    >
                      <MoreVertical size={14} />
                    </button>
                    {openMenuId === lead.id && (
                      <div
                        role="menu"
                        style={{
                          position: 'absolute',
                          right: 0,
                          top: '100%',
                          marginTop: 4,
                          background: 'var(--bg-card)',
                          border: '1px solid var(--border)',
                          borderRadius: 8,
                          boxShadow: 'var(--shadow-md, 0 4px 12px rgba(0,0,0,0.15))',
                          zIndex: 10,
                          minWidth: 140,
                        }}
                      >
                        <button
                          type="button"
                          role="menuitem"
                          className="btn btn-ghost btn-sm"
                          style={{ color: 'var(--danger)', width: '100%', justifyContent: 'flex-start', borderRadius: 8 }}
                          onClick={() => { setOpenMenuId(null); handleDeleteLead(lead); }}
                          title="Delete this lead"
                        >
                          <Trash2 size={14} /> Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
